import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { Fragment, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { Karte, Knopf, Leer } from "@/components/ui"
import { api, fehlertext } from "@/lib/api"
import { istIsoTag, kurz, langesDatum, plusTage, wochentag } from "@/lib/datum"
import { cn } from "@/lib/format"

type Status = "da" | "eingesprungen" | "fehlt" | "nicht erschienen" | "gewechselt"
interface Platz { name: string; status: Status }
interface Schicht {
  nr: number; name: string; modell: string; beginn: string; ende: string; team: string
  arbeitstag: boolean; kalender: string; geprueft: boolean
  zahlen: { soll: number; da: number; fehlt: number; eingesprungen: number; gewechselt: number }
}
interface Stelle { name: string; bereich: string; zellen: Platz[][]; besetzt: number; offen: number }
interface Antwort { eingerichtet: boolean; tag?: string; heute?: string; schichten: Schicht[]; stellen: Stelle[] }

const STIL: Record<Status, { klasse: string; text: string }> = {
  da: { klasse: "bg-good", text: "besetzt" },
  eingesprungen: { klasse: "bg-accent", text: "eingesprungen" },
  fehlt: { klasse: "border-2 border-crit bg-crit/25", text: "fehlt" },
  "nicht erschienen": { klasse: "bg-crit", text: "nicht erschienen" },
  gewechselt: { klasse: "border border-dashed border-muted", text: "in andere Schicht gewechselt" },
}

function Kaestchen({ status, titel }: { status: Status; titel?: string }) {
  return <span title={titel} className={cn("inline-block h-3.5 w-3.5 shrink-0 rounded-[3px]", STIL[status].klasse)} />
}

/** Stellen × Schichten eines Tages nach Plan (Ninox 15-Schichtplanung): je eingeplantem Platz ein Kästchen. */
export function StellenPage() {
  const [params, setParams] = useSearchParams()
  const gewuenscht = params.get("tag")
  const [auswahl, setAuswahl] = useState<{ stelle: string; i: number } | null>(null)
  const abfrage = useQuery({
    queryKey: ["stellen", istIsoTag(gewuenscht) ? gewuenscht : null],
    queryFn: async () =>
      (await api.get<Antwort>("/schichtplan/stellen", { params: istIsoTag(gewuenscht) ? { tag: gewuenscht } : {} })).data,
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  })
  const d = abfrage.data
  const geheZu = (tag: string | null) => {
    setAuswahl(null)
    setParams(tag && tag !== d?.heute ? { tag } : {})
  }

  const besetzt = d?.stellen.reduce((n, s) => n + s.besetzt, 0) ?? 0
  const offen = d?.stellen.reduce((n, s) => n + s.offen, 0) ?? 0
  const gewechselt = d?.schichten.reduce((n, s) => n + s.zahlen.gewechselt, 0) ?? 0
  const arbeitstag = d?.schichten.some((s) => s.arbeitstag) ?? false

  const navigation = d?.tag ? (
    <div className="flex flex-wrap items-center gap-1.5">
      <Knopf onClick={() => geheZu(plusTage(d.tag!, -1))} title="Vortag">
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />{`${wochentag(plusTage(d.tag, -1))} ${kurz(plusTage(d.tag, -1))}`}
      </Knopf>
      {d.tag !== d.heute && <Knopf onClick={() => geheZu(null)}>Heute</Knopf>}
      <Knopf onClick={() => geheZu(plusTage(d.tag!, 1))} title="Folgetag">
        {`${wochentag(plusTage(d.tag, 1))} ${kurz(plusTage(d.tag, 1))}`}<ChevronRight className="h-4 w-4" aria-hidden="true" />
      </Knopf>
      <input
        type="date"
        value={d.tag}
        onChange={(e) => istIsoTag(e.target.value) && geheZu(e.target.value)}
        aria-label="Tag wählen"
        className="rounded-md border border-hair/15 bg-surface px-2 py-1.5 text-sm text-ink"
      />
    </div>
  ) : undefined

  return (
    <main className="mx-auto grid max-w-[1260px] gap-[18px] px-4 pb-12 pt-6">
      <div>
        <h1 className="m-0 text-[clamp(24px,3.2vw,32px)] font-semibold leading-tight">Stellenbesetzung</h1>
        <p className="m-0 mt-1 text-[13px] text-ink2">
          Nach Plan aus der Schichtplanung: je Stelle und Schicht ein Kästchen pro eingeplantem Platz. Klick auf eine Zelle zeigt die Namen, ohne Gründe.
        </p>
      </div>
      <Karte
        titel={d?.tag ? `${langesDatum(d.tag)}${d.tag === d.heute ? " · heute" : ""}` : "Stellenbesetzung"}
        unter={d?.eingerichtet && arbeitstag ? (
          <span>
            <b className="text-ink">{`${besetzt} von ${besetzt + offen} Plätzen besetzt`}</b>
            {offen > 0 && <b className="text-crit">{` · ${offen} offen`}</b>}
            {gewechselt > 0 && ` · ${gewechselt} in andere Schicht gewechselt`}
          </span>
        ) : undefined}
        rechts={navigation}
      >
        {abfrage.isError ? (
          <p className="m-0 text-sm text-crit">{fehlertext(abfrage.error, "Die Schichtplanung ist gerade nicht erreichbar.")}</p>
        ) : !d ? (
          <Leer>Wird geladen …</Leer>
        ) : !d.eingerichtet ? (
          <Leer>Die Schichtplanung ist noch nicht angebunden.</Leer>
        ) : d.schichten.length === 0 ? (
          <Leer>In der Schichtplanung sind noch keine Schichten angelegt.</Leer>
        ) : !arbeitstag ? (
          <Leer>{`Kein Arbeitstag${d.schichten[0]?.kalender ? ` (${d.schichten[0].kalender})` : ""}.`}</Leer>
        ) : d.stellen.length === 0 ? (
          <Leer>Für diesen Tag ist niemand eingeplant.</Leer>
        ) : (
          <>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink2">
              {(Object.keys(STIL) as Status[]).map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5"><Kaestchen status={s} />{STIL[s].text}</span>
              ))}
              <span className="inline-flex items-center gap-1.5"><span className="w-3.5 text-center text-muted">·</span>keine Stelle geplant</span>
            </div>
            <div className={cn("overflow-x-auto", abfrage.isFetching && "opacity-60")}>
              <table className="w-full min-w-[760px] border-collapse text-[13px]">
                <thead>
                  <tr className="align-bottom text-[11.5px] text-muted">
                    <th className="sticky left-0 z-10 bg-surface px-2 py-1.5 text-left font-medium">Stelle</th>
                    {d.schichten.map((s) => (
                      <th key={s.nr} className="px-1.5 py-1.5 text-left font-normal">
                        <div className="text-[12.5px] font-semibold text-ink">{s.name}</div>
                        <div>{`${s.beginn}–${s.ende}`}</div>
                        <div className="truncate">{s.team || " "}</div>
                        {s.arbeitstag ? (
                          <div className={cn("tabular font-medium", s.zahlen.fehlt > 0 ? "text-crit" : "text-good")}>
                            {`${s.zahlen.da}/${s.zahlen.soll}`}
                          </div>
                        ) : (
                          <div title={s.kalender}>frei</div>
                        )}
                      </th>
                    ))}
                    <th className="px-2 py-1.5 text-right font-medium">gesamt</th>
                  </tr>
                </thead>
                <tbody>
                  {d.stellen.map((st, z) => (
                    <Fragment key={st.name}>
                      {st.bereich !== d.stellen[z - 1]?.bereich && (
                        <tr>
                          <td className="sticky left-0 z-10 bg-surface2 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted">{st.bereich}</td>
                          <td colSpan={d.schichten.length + 1} className="bg-surface2" />
                        </tr>
                      )}
                      <tr className="border-t border-grid">
                        <td className={cn("sticky left-0 z-10 bg-surface px-2 py-1.5", st.offen > 0 && "font-medium text-crit")}>{st.name}</td>
                        {st.zellen.map((plaetze, i) => {
                          const aktiv = auswahl?.stelle === st.name && auswahl.i === i
                          return (
                            <td key={d.schichten[i].nr} className="px-1.5 py-1.5">
                              {plaetze.length === 0 || !d.schichten[i].arbeitstag ? (
                                <span className="text-muted">·</span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setAuswahl(aktiv ? null : { stelle: st.name, i })}
                                  title={plaetze.map((p) => `${p.name}: ${STIL[p.status].text}`).join("\n")}
                                  className={cn("flex max-w-[9.5rem] cursor-pointer flex-wrap gap-[3px] rounded p-0.5 hover:bg-surface2", aktiv && "ring-1 ring-accent")}
                                >
                                  {plaetze.map((p, k) => <Kaestchen key={k} status={p.status} />)}
                                </button>
                              )}
                            </td>
                          )
                        })}
                        <td className={cn("px-2 py-1.5 text-right tabular", st.offen > 0 ? "font-medium text-crit" : "text-ink2")}>
                          {`${st.besetzt}/${st.besetzt + st.offen}`}
                        </td>
                      </tr>
                      {auswahl?.stelle === st.name && (
                        <tr>
                          <td colSpan={d.schichten.length + 2} className="px-2 pb-2">
                            <div className="sticky left-2 inline-block rounded-lg bg-surface2 px-3 py-2 text-[13px]">
                              <div className="mb-1 font-medium">
                                {`${st.name} · ${d.schichten[auswahl.i].name}${d.schichten[auswahl.i].team ? ` (${d.schichten[auswahl.i].team})` : ""}`}
                              </div>
                              <ul className="m-0 grid list-none gap-1 p-0">
                                {st.zellen[auswahl.i].map((p, k) => (
                                  <li key={k} className="flex items-center gap-2">
                                    <Kaestchen status={p.status} />
                                    <b>{p.name}</b>
                                    <span className={cn(p.status === "fehlt" || p.status === "nicht erschienen" ? "text-crit" : "text-ink2")}>{STIL[p.status].text}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Karte>
    </main>
  )
}
